if (!customElements.get("bonus-item")) {
  customElements.define(
    "bonus-item",
    class BonusItem extends HTMLElement {
      constructor() {
        super();

        this.attachShadow({ mode: "open" });
        const attrBool = (v) => v === "true" || v === "1";

        // Order subtotal thresholds for order-level bonus
        // Prefer explicit attributes on the `<bonus-item>` element if provided;
        // otherwise attempt to read the progress bar's data attributes (rendered server-side),
        // and finally fall back to the hard-coded defaults.
        const barEl = document.querySelector(".cart-reward-progress__bar");
        const attrLow = this.getAttribute("data-low-threshold-cents");
        const attrLowBonus = this.getAttribute("data-low-bonus-dollar");
        const barLow = barEl && barEl.getAttribute("data-low-threshold-cents");
        const barLowBonus =
          barEl && barEl.getAttribute("data-low-bonus-dollar");

        this.thresholdLowCents =
          parseInt(attrLow || barLow || "9900", 10) || 9900;
        // high threshold defaults to 14900, but when second tier is disabled we'll
        // treat the high threshold as equal to the low threshold elsewhere.
        this.thresholdHighCents =
          parseInt(
            this.getAttribute("data-high-threshold-cents") || "14900",
            10,
          ) || 14900;
        // lowest bonus dollar (decimal) used for the low-tier reward (e.g. 5 => $5 or 500 points)
        this.lowBonusDollar =
          parseFloat(attrLowBonus || barLowBonus || "5") || 5;

        this.sectionId = this.getAttribute("section-id") || "default";
        this.bonusItemId = this.getAttribute("bonus-item-id") || "";
        this.rewardItemId = this.getAttribute("reward-item-id") || "";
        this.bonusItemDesc = this.getAttribute("bonus-item-description") || "";
        this.discountable = this.getAttribute("discountable") === "true";
        this.gc5Id = this.getAttribute("gc-5-id") || "";
        this.gc10Id = this.getAttribute("gc-10-id") || "";
        this.bp500Id = this.getAttribute("bp-500-id") || "";
        this.bp1000Id = this.getAttribute("bp-1000-id") || "";

        // Feature flag to enable order-level tier bonuses (from shop metafield)
        this.enableTierBonus = attrBool(
          this.getAttribute("data-enable-tier-bonus") || "false",
        );
        // Optionally disable the second (high) tier (from shop metafield)
        this.disableSecondTier = attrBool(
          this.getAttribute("data-disable-second-tier") || "false",
        );

        // If second tier is disabled the effective high threshold should match the low threshold
        if (this.disableSecondTier)
          this.thresholdHighCents = this.thresholdLowCents;

        // Pass in server side info so we know which rewards are in cart at render time
        const cartHasGc5 = attrBool(this.getAttribute("data-cart-has-gc-5"));
        const cartHasGc10 = attrBool(this.getAttribute("data-cart-has-gc-10"));
        const cartHasBp500 = attrBool(
          this.getAttribute("data-cart-has-bp-500"),
        );
        const cartHasBp1000 = attrBool(
          this.getAttribute("data-cart-has-bp-1000"),
        );

        // If the second tier is disabled, ignore the high-order bonus
        this.hasHighOrderBonusInCart =
          !this.disableSecondTier && (cartHasGc10 || cartHasBp1000);
        this.hasLowOrderBonusInCart =
          this.hasHighOrderBonusInCart || cartHasGc5 || cartHasBp500;

        this.iconItem =
          this.getAttribute("data-icon-item") ||
          "https://cdn.shopify.com/s/files/placeholder_badge.png";
        this.iconOrder =
          this.getAttribute("data-icon-order") ||
          "https://cdn.shopify.com/s/files/placeholder_order.png";

        this.cartSubtotalCents =
          parseInt(this.getAttribute("cart-subtotal") || "0", 10) || 0;
        this.itemPriceCents =
          parseInt(this.getAttribute("item-price-cents") || "0", 10) || 0;

        this.hasValidBonusItem = this.bonusItemId && !this.discountable;

        // This is how we know what level the item level rewards are
        const match = this.bonusItemDesc.match(/\$(\d+)/);
        this.itemLevelValue = match ? parseInt(match[1], 10) : 0;

        // Get loyalty status and active discount code from global.js (localStorage)
        const savedLoyalty = localStorage.getItem("_tcloyalty");
        const savedDiscount = localStorage.getItem("_tcdiscount");
        this.isLoyaltyMember = !!(
          savedLoyalty &&
          savedLoyalty !== "NONE" &&
          savedLoyalty !== "INACTIVE"
        );
        this.showRewards =
          savedLoyalty !== "INACTIVE" ||
          !savedDiscount ||
          savedDiscount !== "GPSLT";
        if (savedLoyalty === "NONE") this.showRewards = false;

        this.shadowRoot.innerHTML = `
          <style>
            :host {
              display:inline-flex;
              align-items:center;
              margin-left:0.8rem;
              padding-top:4px;
              line-height:1;
            }
            .container-slot {
              display:inline-flex;
              align-items:center;
              flex-wrap:wrap;
              gap:10px;
            }
            .container-slot > span {
              margin-right:0;
              display:inline-flex;
              align-items:center;
            }
            .visually-hidden { display:none !important; }
            .bonus-rewards {
              font-family: Arial, sans-serif;
              max-width:550px;
              margin:0;
              display:inline-flex;
              align-items:center;
            }
            .bonus-box {
              background:#f5f0e6;
              border-radius:5px;
              padding:10px;
              display:flex;
              align-items:center;
              box-shadow:2px 2px 5px rgba(0,0,0,.1);
              letter-spacing:.5px;
              margin-right:10px;
            }
            .bonus-box:not(.bonus-order-box) { margin-right:10px; }
            .bonus-order-box { padding-left:2px; }
            .bonus-icon,
            .bonus-order-icon {
              width:60px;
              height:60px;
              margin-right:5px;
              display:flex;
              align-items:center;
              justify-content:center;
            }
            .bonus-order-icon { width:68px; }
            .bonus-box img { display:block; max-height:60px; }
            .bonus-text {
              font-size:14px;
              color:#333;
              margin-left:5px;
              padding-right:5px;
              display:flex;
              flex-direction:column;
              justify-content:center;
              line-height:1;
            }
            .bonus-text br { display:none; }
            .bonus-text strong,
            .bonus-text em {
              display:block;
              margin:0;
              padding:0;
              line-height:1.2;
            }
            .bonus-text strong { font-size:16px; color:#000; font-weight:600; }
            .bonus-text em { font-size:12px; color:#777; font-style:normal; }
            [data-order-box-wrapper][data-visible="false"] { display:none !important; }
          </style>
          <span class="container-slot"></span>
        `;

        this.renderWithSubtotal();
      }

      connectedCallback() {
        this.registerCartEvents();
        if (!this.hasServerOrderBonusState()) {
          this.checkOrderLevelBonusesInCart();
        }
      }

      hasServerOrderBonusState() {
        if (!this.hasAttribute("cart-subtotal")) return false;

        return [
          [this.gc5Id, "data-cart-has-gc-5"],
          [this.gc10Id, "data-cart-has-gc-10"],
          [this.bp500Id, "data-cart-has-bp-500"],
          [this.bp1000Id, "data-cart-has-bp-1000"],
        ].every(([id, attr]) => !id || this.hasAttribute(attr));
      }

      // Check if order-level bonus already exist in cart
      checkOrderLevelBonusesInCart() {
        const highIds = this.disableSecondTier
          ? []
          : [this.gc10Id, this.bp1000Id].filter(Boolean);
        const lowIds = [this.gc5Id, this.bp500Id].filter(Boolean);
        if (!highIds.length && !lowIds.length) return;

        window
          .fetch("/cart.js")
          .then((r) => r.json())
          .then((cart) => {
            if (!cart || !Array.isArray(cart.items)) return;

            let foundHigh = false;
            let foundLow = false;
            cart.items.forEach((it) => {
              const vid = String(it.id || it.variant_id || "");
              if (!foundHigh && highIds.includes(vid)) foundHigh = true;
              if (!foundLow && lowIds.includes(vid)) foundLow = true;
            });

            let changed = false;
            if (foundHigh && !this.hasHighOrderBonusInCart) {
              this.hasHighOrderBonusInCart = true;
              changed = true;
            }
            if (foundLow && !this.hasLowOrderBonusInCart) {
              this.hasLowOrderBonusInCart = true;
              changed = true;
            }

            if (changed) {
              this.cartSubtotalCents =
                cart.items_subtotal_price || this.cartSubtotalCents;
              this.renderWithSubtotal();
            }
          })
          .catch(() => {
            /* ignore */
          });
      }

      // Set events based on changes to cart
      registerCartEvents() {
        this.__onCartAdd = () => this.hideOrderLevelBonus();

        document.addEventListener("cart:item:added", this.__onCartAdd);
      }

      // Clear events
      unregisterCartEvents() {
        document.removeEventListener("cart:item:added", this.__onCartAdd);
      }

      // Forces order level bonus to disappear
      hideOrderLevelBonus() {
        const orderBox =
          this.shadowRoot && this.shadowRoot.querySelector(".bonus-order-box");
        if (orderBox && !orderBox.__hiddenManually) {
          orderBox.__hiddenManually = true;
          const wrapperSpan = orderBox.closest("span");
          if (wrapperSpan) wrapperSpan.style.display = "none";
          // Remove only the order-level variant input
          const orderVariant = this.shadowRoot.querySelector(
            'input[data-bonus-variant][data-order-level="1"]',
          );
          if (orderVariant) orderVariant.remove();
        }
      }

      // this gets the GPSLT gift card icon
      getIcon() {
        return this.itemLevelValue > 0
          ? `ecert-icon-item-page${this.itemLevelValue}.png`
          : "ecert-icon-item-pageGeneric.png";
      }

      // Determine which order-level bonus they qualify for
      computeQualification(subtotalCents, prospectiveItemCents) {
        const subtotal = subtotalCents;
        const potential = subtotal + prospectiveItemCents;

        const crossesLow =
          subtotal < this.thresholdLowCents &&
          potential >= this.thresholdLowCents;
        // If second tier is disabled, never consider the high tier
        const crossesHigh = this.disableSecondTier
          ? false
          : subtotal < this.thresholdHighCents &&
            potential >= this.thresholdHighCents;

        if (crossesHigh)
          return {
            qualifiesDollar: 10,
            thresholdUsed: this.thresholdHighCents / 100,
          };
        if (crossesLow)
          return {
            qualifiesDollar: this.lowBonusDollar,
            thresholdUsed: this.thresholdLowCents / 100,
          };
        return { qualifiesDollar: 0, thresholdUsed: 0 };
      }

      // Create a label for the order-level box (points vs dollars)
      getOrderBoxLabel(qualifiesDollar) {
        if (!qualifiesDollar) return "";
        // points display
        if (this.isLoyaltyMember || this.showRewards) {
          const pts = Math.round(qualifiesDollar * 100);
          return {
            primary: `${pts} Points`,
            secondary: `on orders $${qualifiesDollar === 10 ? this.thresholdHighCents / 100 : this.thresholdLowCents / 100}+`,
          };
        }
        // Gift card / dollar reward
        return {
          primary: `$${qualifiesDollar} Bonus`,
          secondary: `on orders $${qualifiesDollar === 10 ? this.thresholdHighCents / 100 : this.thresholdLowCents / 100}+`,
        };
      }

      // Create a label for the item-level box
      getItemBoxLabel(pointsValue) {
        if (this.isLoyaltyMember || this.showRewards) {
          return {
            primary: `Earn ${pointsValue * 100} Points`,
            secondary: `for future purchases`,
          };
        }
        // Gift card
        return {
          primary: `$${pointsValue} Gift Card`,
          secondary: `for future purchases`,
        };
      }

      // Render the buttons on the page
      renderWithSubtotal() {
        const subtotal = this.cartSubtotalCents;
        const { qualifiesDollar, thresholdUsed } = this.computeQualification(
          subtotal,
          this.itemPriceCents,
        );

        let effectiveVariantId = this.bonusItemId;
        let effectiveOrderVariantId = this.bonusItemId;

        if (!this.isLoyaltyMember) {
          effectiveVariantId = this.rewardItemId;
          if (qualifiesDollar === 10 && this.gc10Id) {
            effectiveOrderVariantId = this.gc10Id;
          } else if (
            Number(qualifiesDollar) === Number(this.lowBonusDollar) &&
            this.gc5Id
          ) {
            effectiveOrderVariantId = this.gc5Id;
          }
        } else {
          if (qualifiesDollar === 10 && this.bp1000Id) {
            effectiveOrderVariantId = this.bp1000Id;
          } else if (
            Number(qualifiesDollar) === Number(this.lowBonusDollar) &&
            this.bp500Id
          ) {
            effectiveOrderVariantId = this.bp500Id;
          }
        }

        const containerSlot = this.shadowRoot.querySelector(".container-slot");
        if (!containerSlot) return;

        const showOrderBoxLogic =
          (qualifiesDollar === 10 && !this.hasHighOrderBonusInCart) ||
          (Number(qualifiesDollar) === Number(this.lowBonusDollar) &&
            !this.hasLowOrderBonusInCart &&
            !this.hasHighOrderBonusInCart);

        const qualifiesAny = qualifiesDollar > 0;
        const showItemBox =
          this.hasValidBonusItem &&
          this.showRewards &&
          !this.sectionId.includes("cart-items");
        const showStandard = !this.showRewards && this.rewardItemId !== "";

        const itemLabel = this.getItemBoxLabel(this.itemLevelValue);
        const orderLabel = this.getOrderBoxLabel(qualifiesDollar);

        // If TEST_MODE
        const allowOrderRewards =
          !this._hideOrderRewardsInTest && this.enableTierBonus;

        containerSlot.innerHTML = `
          <span class="${showItemBox ? "bonus-rewards" : "visually-hidden"}">
            <div class="bonus-box">
              <span class="bonus-icon">
                <img width="60" src="${this.iconItem}" alt="Bonus Item">
              </span>
              <span class="bonus-text">
                <strong>${itemLabel.primary}</strong><br>
                <em>${itemLabel.secondary}</em>
              </span>
            </div>
          </span>

          <span class="${showStandard && !showItemBox ? "" : "visually-hidden"}">
            <span id="Bonus-Item-${this.sectionId}">
              <img src="https://cdn.shopify.com/s/files/1/0647/0506/9193/files/${this.getIcon()}" alt="Bonus Item">
            </span>
          </span>

          ${this.hasValidBonusItem ? `<input type="hidden" value="${effectiveVariantId}" data-bonus-variant />` : ""}

          ${
            qualifiesAny && allowOrderRewards
              ? `
            <span class="bonus-rewards" data-order-box-wrapper data-visible="${showOrderBoxLogic ? "true" : "false"}">
              <div class="bonus-box bonus-order-box">
                <span class="bonus-icon bonus-order-icon">
                  <img width="68" src="${this.iconOrder}" alt="Order Level Bonus">
                </span>
                <span class="bonus-text">
                  <strong>${orderLabel.primary}</strong><br>
                  <em>${orderLabel.secondary}</em>
                </span>
              </div>
            </span>
            ${showOrderBoxLogic ? `<input type="hidden" value="${effectiveOrderVariantId}" data-bonus-variant data-order-level="1" />` : ""}
          `
              : ""
          }

          <input type="hidden" data-bonus-threshold value="${thresholdUsed || ""}">
        `;
      }
    },
  );
}
